import imageCompression from 'browser-image-compression';

export interface CompressedImage {
  file: File;
  previewUrl: string;
}

/**
 * `useWebWorker` var `!Capacitor.isNativePlatform()` — altså AV på Android og iOS.
 * Konsekvensen var at browser-image-compression dekodet, skalerte og re-encodet
 * fulloppløste telefonbilder (12–50 MP) på MAIN THREAD, så hele UI-et frøs mens
 * brukeren la inn en plass. Det var den Android-spesifikke halvparten av
 * treghets-rapporten 23.08.2026.
 *
 * Workeren er på for alle plattformer nå. Skulle den ikke være tilgjengelig (streng
 * CSP, gammel WebView), fanger `compressImage` feilen og prøver på nytt uten worker
 * — se `compressWithWorkerFallback`. Vi mister da ytelsen, men aldri bildet.
 */
const COMPRESS_OPTIONS = {
  maxWidthOrHeight: 1280,
  maxSizeMB: 1.0,
  useWebWorker: true,
  fileType: 'image/jpeg' as const,
  initialQuality: 0.75,
};

/**
 * Filen kan ikke dekodes av nettleseren, og et <img> vil derfor aldri kunne tegne
 * den. Typisk kamera-RAW (.dng/.cr2/.nef/.arw) som Androids filvelger tilbyr under
 * `accept="image/*"`. Kastes av compressImage slik at kallstedet kan avvise filen
 * i stedet for å laste opp en original som blir et brutt bilde-ikon for alltid.
 */
export class UnsupportedImageError extends Error {
  constructor(public readonly fileName: string) {
    super(`Bildeformatet kan ikke vises: ${fileName}`);
    this.name = 'UnsupportedImageError';
  }
}

/**
 * En melding besto kun av bilder, og ingen av dem lot seg klargjøre for
 * opplasting. Kastes i stedet for å skrive en melding uten tekst og uten bilder —
 * en blank boble hos mottaker som avsenderen tror ble sendt.
 */
export class UnsendableImagesError extends Error {
  readonly code = 'image/unsendable';
  constructor(public readonly skipped: string[]) {
    super(`Ingen av bildene kunne sendes: ${skipped.join(', ') || 'ukjent fil'}`);
    this.name = 'UnsendableImagesError';
  }
}

const DECODE_PROBE_TIMEOUT_MS = 4000;

/**
 * Kan nettleseren faktisk tegne denne filen? Vi tester dekoding i stedet for å
 * svarteliste filendelser — HEIC dekodes f.eks. fint i Safari, men ikke i Chrome.
 */
async function isDecodable(file: File): Promise<boolean> {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(file);
      bitmap.close?.();
      return true;
    } catch {
      return false;
    }
  }
  // Eldre WebView uten createImageBitmap: fall tilbake til <img>-dekoding.
  // Timeout, ellers henger opplastingen i det stille dersom verken onload eller
  // onerror fyrer. Vi er allerede i feilgrenen, så «vet ikke» behandles som nei.
  return new Promise<boolean>(resolve => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    let done = false;
    const finish = (ok: boolean) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      URL.revokeObjectURL(url);
      resolve(ok);
    };
    const timer = setTimeout(() => finish(false), DECODE_PROBE_TIMEOUT_MS);
    img.onload = () => finish(true);
    img.onerror = () => finish(false);
    img.src = url;
  });
}

/** Formater nettleseren kan tegne direkte, og som derfor ikke må konverteres. */
const WEB_SAFE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/avif', 'image/gif']);

/**
 * Filendelser enhver nettleser kan tegne. Filvelgeren på Android gir ofte tom
 * `type` (og Capacitor-broen setter av og til `application/octet-stream`), så
 * MIME-typen alene underkjenner helt vanlige bilder.
 */
const WEB_SAFE_EXTENSIONS = /\.(jpe?g|png|webp|avif|gif|bmp)$/i;

/** Er dette et format vi vet nettleseren kan tegne — uansett hva MIME-typen påstår? */
const isKnownWebFormat = (file: File): boolean =>
  WEB_SAFE_TYPES.has(file.type) || WEB_SAFE_EXTENSIONS.test(file.name);

/**
 * Komprimer med web worker, og fall tilbake til main thread hvis workeren ikke lot
 * seg starte. Workeren lastes som blob-URL, noe en streng CSP eller en gammel
 * WebView kan nekte — da skal bildet fortsatt bli komprimert, bare tregere.
 *
 * Skiller bevisst IKKE på feiltype: enhver feil med worker på gir ett nytt forsøk
 * uten. Er filen genuint udekodbar (kamera-RAW), feiler forsøk nummer to på samme
 * måte, og kalleren håndterer det som før.
 */
async function compressWithWorkerFallback(
  file: File,
  options: typeof COMPRESS_OPTIONS | typeof THUMB_OPTIONS,
): Promise<File> {
  try {
    return await imageCompression(file, options);
  } catch (err) {
    if (!options.useWebWorker) throw err;
    console.warn('[imageCompression] web worker feilet, prøver på main thread:', err);
    return await imageCompression(file, { ...options, useWebWorker: false });
  }
}

export const compressImage = async (file: File): Promise<CompressedImage> => {
  try {
    const compressed = await compressWithWorkerFallback(file, COMPRESS_OPTIONS);
    const previewUrl = URL.createObjectURL(compressed);
    return { file: compressed, previewUrl };
  } catch (err) {
    // browser-image-compression dekoder via canvas. Feiler den, er filen som oftest
    // et format nettleseren ikke forstår. Å falle tilbake til originalen ukritisk
    // lastet tidligere opp 40 MB .dng-filer som <img> aldri kunne tegne.
    //
    // Men «formatet kan ikke vises» er en påstand om FORMATET. Er filen en vanlig
    // JPEG eller PNG, er formatet aldri problemet: da feilet komprimeringen av en
    // annen grunn (minnepress på mobil, web worker som dør), og isDecodable —
    // som dekoder på nøyaktig samme måte — feiler gjerne av samme grunn. Å avvise
    // filen der kaster et fullt brukbart bilde.
    if (!isKnownWebFormat(file) && !(await isDecodable(file))) {
      console.warn('[compressImage] avviser fil nettleseren ikke kan tegne:', file.name, file.type, file.size);
      throw new UnsupportedImageError(file.name);
    }
    console.error('[compressImage] Compression failed, bruker original:', file.name, file.type, file.size, err);
    // Filen kan vises, men er ukomprimert — logg så vi ser om dette blir vanlig.
    return { file, previewUrl: URL.createObjectURL(file) };
  }
};

/**
 * Over denne grensen komprimerer vi uansett. Under den er filen liten nok til at
 * en ny runde bare koster kvalitet. COMPRESS_OPTIONS sikter på 1 MB, så 1,5 MB gir
 * slingringsmonn for bilder som såvidt bommet på målet.
 */
const ALREADY_SMALL_ENOUGH_BYTES = 1.5 * 1024 * 1024;

/**
 * Garanter at en fil er trygg å laste opp: nettleser-tegnbar og rimelig stor.
 * Idempotent — et allerede komprimert bilde slippes gjennom urørt, så gjentatte
 * kall ikke degraderer kvaliteten.
 *
 * Ment å kalles INNE i opplastingsfunksjonene. Komprimering lå tidligere kun hos
 * kallerne (commit 0a940cf), og alt som ble lastet opp før den kom på plass ligger
 * i Storage på 8–15 MB. Med garantien i selve opplastingen kan en ny kallsti ikke
 * gjøre samme feil på nytt.
 *
 * Kaster UnsupportedImageError for formater nettleseren ikke kan dekode.
 */
export const ensureCompressedImage = async (file: File): Promise<File> => {
  if (WEB_SAFE_TYPES.has(file.type) && file.size <= ALREADY_SMALL_ENOUGH_BYTES) return file;
  const { file: compressed } = await compressImage(file);
  return compressed;
};

const THUMB_OPTIONS = {
  maxWidthOrHeight: 256,
  maxSizeMB: 0.06,
  useWebWorker: true,
  fileType: 'image/jpeg' as const,
  initialQuality: 0.7,
};

/**
 * Lag en liten miniatyr av et allerede validert bilde. Brukes til å skrive
 * `thumbs/<sti>` sammen med originalen, slik at listevisninger (getThumbUrl)
 * slipper å laste ned flere megabyte inn i en 36×36-rute.
 * Returnerer null hvis miniatyren ikke lot seg lage — kallstedet skal da bare
 * hoppe over den, aldri feile opplastingen av originalen.
 */
export const makeThumbnail = async (file: File): Promise<File | null> => {
  try {
    return await compressWithWorkerFallback(file, THUMB_OPTIONS);
  } catch (err) {
    console.warn('[makeThumbnail] kunne ikke lage miniatyr:', err);
    return null;
  }
};

export interface CompressImagesResult {
  files: File[];
  /** Filnavn som ble hoppet over fordi nettleseren ikke kan dekode dem. */
  skipped: string[];
}

/**
 * Komprimer flere bilder sekvensielt (unngår minne-topper på mobil). Filer
 * nettleseren ikke kan dekode hoppes over og rapporteres i `skipped` — kallstedet
 * bør si fra til brukeren.
 */
export const compressImagesDetailed = async (files: File[]): Promise<CompressImagesResult> => {
  const compressedFiles: File[] = [];
  const skipped: string[] = [];
  for (const file of files) {
    try {
      const { file: compressed } = await compressImage(file);
      compressedFiles.push(compressed);
    } catch (err) {
      if (err instanceof UnsupportedImageError) {
        skipped.push(file.name);
        continue;
      }
      throw err;
    }
  }
  return { files: compressedFiles, skipped };
};

/** Bakoverkompatibel variant: returnerer bare filene som kan vises. */
export const compressImages = async (files: File[]): Promise<File[]> =>
  (await compressImagesDetailed(files)).files;
